import React, { ReactNode } from 'react';
import { VibeshipContext, type VibeshipConfig } from '../contexts/VibeshipContext';
import { VibeshipClientProvider } from './VibeshipClientProvider';

// Browser-safe default config
const browserDefaultConfig: VibeshipConfig = {
  scanPaths: ['./docs', './README.md', './.tickets'],
  include: ['**/*.md', '**/*.mdx'],
  exclude: ['**/node_modules/**', '**/.next/**', '**/.git/**', '**/dist/**', '**/build/**'],
  features: {
    ai: true,
    tasks: true,
    fileEditing: true,
    commandPalette: true,
    devtools: true,
  },
  ui: {
    theme: 'system' as const,
    hotkey: 'cmd+k',
    position: 'fixed' as const,
    showInProduction: false,
  },
  api: {
    basePath: '/api/vibeship',
    timeout: 30000,
  },
  cache: {
    enabled: true,
    ttl: 300000,
    maxSize: 1000,
  },
  experimental: {}
};

// Simple merge function for browser use
function simpleMerge(target: any, ...sources: any[]): any {
  if (!sources.length) return target;
  const source = sources.shift();
  
  if (target && source && typeof target === 'object' && typeof source === 'object') {
    for (const key in source) {
      if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
        if (!target[key]) target[key] = {};
        simpleMerge(target[key], source[key]);
      } else {
        target[key] = source[key];
      }
    }
  }
  
  return simpleMerge(target, ...sources);
}

export interface VibeshipServerProviderProps {
  children: ReactNode;
  config?: Partial<VibeshipConfig>;
}

/**
 * Server-side provider that doesn't use any client-side hooks.
 * This can be used in the root layout without making it a client component.
 */
export function VibeshipServerProvider({ children, config = {} }: VibeshipServerProviderProps) {
  const mergedConfig = simpleMerge({}, browserDefaultConfig, config);
  
  // Server-side context value without any hooks
  const contextValue = {
    config: mergedConfig,
    isReady: true, // Always ready on server side
  };
  
  return (
    <VibeshipContext.Provider value={contextValue}>
      {children}
      <VibeshipClientProvider />
    </VibeshipContext.Provider>
  );
}